import type { ExecutionMode } from '@/components/worktree-controls';
import { localWorkspaceRoot } from '@/lib/cloud-runtime/cloud-workspace';
import { basename, isCloudFolder } from '@/lib/desktop-paths';
import type { ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import type { RepositoryOption } from '@/lib/desktop-shell/repository-options';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { openDirectoryDialog } from '@/lib/electron-bridge';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import {
  checkoutSourceControlRepository,
  resolveSourceControlRepository,
} from '@/lib/source-control-connector';

/** Where the cloud keeps a machine's folder that has no forge (`localWorkspaceRoot`). */
const LOCAL_FOLDER_ROOT = '/workspace/repository/local/';

/**
 * The ways on from a cloud start refused because Crowdex Cloud keeps no copy of the project
 * (`cloud_project_missing`): its folder on this computer, when one is known, the project another
 * folder joins, and the forge whose copy the cloud can start from.
 */
export type CloudStartRecovery = {
  /** The project's folder the cloud was asked for. */
  path: string;
  name: string;
  /** This computer's folder of the project, to continue there. */
  localPath: string | null;
  /** The project a folder chosen here joins (`null`: none known). */
  logicalWorkspaceId: string | null;
  /** The forge repository the cloud can start from instead. */
  forge: { fullName: string; remote: string | null } | null;
};

/**
 * What can be offered for a project the cloud has no copy of, from the repositories of "Where it
 * runs": its folder on this computer is the one of the same project, else of the same forge
 * repository, else the folder the cloud would have named the same.
 */
export function cloudStartRecovery(
  path: string,
  repositories: readonly RepositoryOption[],
): CloudStartRecovery {
  const listed = repositories.find((repository) => repository.path === path);
  const origin = listed?.origin;
  const logicalWorkspaceId = origin?.logicalWorkspaceId ?? null;
  // A folder without a forge is the cloud's `local/<name>`: no forge repository by that name.
  const forgeless = path.startsWith(LOCAL_FOLDER_ROOT);
  const fullName = forgeless ? null : sourceControlRepositoryFullName(path);
  const forge = forgeless
    ? null
    : (origin?.forge ?? (fullName === null ? null : { fullName, remote: null }));
  const here = repositories.filter(
    (repository) => !isCloudFolder(repository.path) && repository.origin?.onThisComputer !== false,
  );
  const local =
    (logicalWorkspaceId === null
      ? undefined
      : here.find((repository) => repository.origin?.logicalWorkspaceId === logicalWorkspaceId)) ??
    (forge === null
      ? undefined
      : here.find((repository) => repository.origin?.forge?.fullName === forge.fullName)) ??
    (forgeless
      ? here.find((repository) => localWorkspaceRoot(basename(repository.path)) === path)
      : undefined);
  return {
    path,
    name: listed?.name ?? basename(path),
    localPath: local?.path ?? null,
    logicalWorkspaceId,
    forge,
  };
}

/** What the recovery card offers, by where the page runs, and its actions. */
export type CloudProjectMissingActions = {
  recovery: CloudStartRecovery;
  /** This computer can run the conversation (the desktop app). */
  local: boolean;
  onContinueHere: (path: string) => void;
  onChooseFolder: () => void;
  onUseCloudCopy: () => void;
  onChooseRepository: () => void;
};

type RecoveryDeps = {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  currentRuntimeMode: () => ExecutionMode;
  changeExecutionMode: (mode: ExecutionMode) => Promise<void>;
  startProjectConversation: (path: string | null) => void;
  chooseWorkspaceForHandoff: (input: {
    logicalWorkspaceId: string;
    name?: string;
  }) => Promise<string | null>;
  openDirectoryDialog: () => Promise<string | null>;
};

/**
 * The recovery card of the folder the cloud has no copy of (`cloudProjectMissing`), or `null`
 * when the cloud start was not refused for that.
 */
export function cloudProjectMissingCard(
  input: Omit<RecoveryDeps, 'openDirectoryDialog'> & {
    path: string | null;
    repositories: readonly RepositoryOption[];
    local: boolean;
    onChooseRepository: () => void;
  },
): CloudProjectMissingActions | null {
  if (input.path === null) return null;
  const recovery = cloudStartRecovery(input.path, input.repositories);
  const actions = createCloudStartRecoveryActions({ ...input, openDirectoryDialog });
  return {
    recovery,
    local: input.local,
    onContinueHere: (path) => void actions.continueOnThisComputer(path),
    onChooseFolder: () => void actions.chooseFolder(recovery),
    // A machine reaches the cloud's forges through its Crowdex Cloud link.
    onUseCloudCopy: () =>
      void actions.startFromCloudCopy(recovery, { throughCloudLink: isLocalMachine() }),
    onChooseRepository: input.onChooseRepository,
  };
}

/**
 * The recovery card's actions: continue on this computer in a folder (the project's, or one
 * chosen and linked to it), or start the cloud from the forge's copy of the repository.
 */
export function createCloudStartRecoveryActions(deps: RecoveryDeps) {
  /** The conversation runs in `path` on this computer: Runs in › This computer, with it. */
  async function continueOnThisComputer(path: string): Promise<void> {
    deps.store.getState().setCloudProvisionError(null);
    // A new conversation coming back from the cloud returns to the last folder: this one.
    localStorage.setItem(WORKSPACE_KEY, path);
    if (deps.currentRuntimeMode() === 'cloud') await deps.changeExecutionMode('local');
    else deps.startProjectConversation(path);
  }

  /** A folder of this computer, joined to the project when it has one, to continue in. */
  async function chooseFolder(recovery: CloudStartRecovery): Promise<boolean> {
    const path =
      recovery.logicalWorkspaceId === null
        ? await deps.openDirectoryDialog()
        : await deps.chooseWorkspaceForHandoff({
            logicalWorkspaceId: recovery.logicalWorkspaceId,
            name: recovery.name,
          });
    if (path === null) return false;
    await continueOnThisComputer(path);
    return true;
  }

  /** The cloud starts from the forge's copy of the repository (its project, made if need be). */
  async function startFromCloudCopy(
    recovery: CloudStartRecovery,
    options: { throughCloudLink: boolean },
  ): Promise<boolean> {
    if (recovery.forge === null) return false;
    const conversation = deps.conversations.shown();
    deps.store.getState().setCloudProvisionError(null);
    conversation.update({ cloudRepositoryBusy: true });
    try {
      const repository = await resolveSourceControlRepository(
        recovery.forge.remote ?? recovery.forge.fullName,
        options.throughCloudLink,
      );
      conversation.place({
        workspace: await checkoutSourceControlRepository(repository, conversation.chatId),
        executionMode: 'cloud',
      });
      return true;
    } catch (error) {
      conversation.whileShown((state) => state.setCloudProvisionError(errorMessage(error)));
      return false;
    } finally {
      conversation.update({ cloudRepositoryBusy: false });
    }
  }

  return { continueOnThisComputer, chooseFolder, startFromCloudCopy };
}
